// A2F-650 follow-up: standalone post-signup confirmation page.
//
// Replaces the inline SuccessPanel that used to render below Step 5 on
// /. The two install cards on the old panel were stale (the AppSource
// link was a search URL, not a listing) and showing install
// instructions before the user has confirmed their email is the wrong
// moment anyway — install belongs on /install, which is where the
// confirmation link lands them.
//
// Email is passed via react-router location state so we don't have to
// expose it in a query param. A user landing here directly (refresh,
// shared URL) sees the generic copy with no email substring rather
// than a broken page.

import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Mail } from 'lucide-react'
import { TopNav } from '../components/onboard/TopNav'
import { Footer } from '../components/onboard/Footer'
import { useAuth } from '../lib/AuthContext'

interface LocationState {
  email?: string
}

// A2F-922: Google Ads conversion. Module-level set guards against
// re-firing if the user navigates back/forward to this page within
// the same SPA session. A full reload starts a fresh set; Google
// dedups server-side via transaction_id.
//
// transaction_id is a SHA-256 hex of the lowercased, trimmed email
// — Google's documented enhanced-conversions format. Sending the
// plaintext email would hand raw PII to a third-party ad network;
// hashing dedupes equivalently because Google hashes on their side.
const reportedConversionEmails = new Set<string>()

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export function CheckEmailPage() {
  const { session } = useAuth()
  const location = useLocation()
  const state = (location.state as LocationState | null) ?? null
  const email = state?.email

  useEffect(() => {
    if (!email) return
    if (reportedConversionEmails.has(email)) return
    if (typeof window.gtag !== 'function') return
    // Add synchronously before the async hash resolves so a re-render
    // during the in-flight hash can't double-fire the conversion.
    reportedConversionEmails.add(email)
    void (async () => {
      const transactionId = await sha256Hex(email.trim().toLowerCase())
      window.gtag?.('event', 'conversion', {
        send_to: 'AW-18193657358/fP3eCNu2t7QcEI7ctOND',
        value: 1.0,
        currency: 'USD',
        transaction_id: transactionId,
      })
    })()
    // Mount-only; email is the dedupe key, not a reactive dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen bg-brand-canvas text-brand-charcoal font-sans flex flex-col">
      <TopNav session={session} />

      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <div className="rounded-full h-14 w-14 grid place-items-center mx-auto mb-5 bg-brand-orange/10 text-brand-orange">
            <Mail size={24} />
          </div>
          <h1 className="text-2xl font-semibold mb-3">Check your email</h1>
          {email ? (
            <p className="text-sm text-slate-600 leading-relaxed">
              We sent a confirmation link to{' '}
              <strong className="text-brand-charcoal">{email}</strong>. Click
              it to activate your account and land on the install page with
              your ribbon ready to go.
            </p>
          ) : (
            <p className="text-sm text-slate-600 leading-relaxed">
              Check your inbox for the confirmation link. Click it to activate
              your account and land on the install page with your ribbon ready
              to go.
            </p>
          )}
          <p className="text-xs text-slate-500 mt-8">
            Didn&apos;t get it? Check your spam folder, or{' '}
            <Link to="/" className="text-brand-orange hover:underline">
              try again
            </Link>
            .
          </p>
        </div>
      </main>

      <Footer />
    </div>
  )
}
